import { useCallback, useMemo, useState } from "react";

export interface IAnswerStorageValue {
  [questionId: number]: {
    [roundNo: number]: string | null;
  };
}

const useAnswerStorage = (): [
  {
    storageValue: IAnswerStorageValue;
    tempValue: IAnswerStorageValue;
  },
  {
    setQuestionIds: (
      questionIds: number[],
      roundNo: number,
    ) => IAnswerStorageValue;
    setTempValue: (questionId: number, roundNo: number, value: string) => void;
    saveValues: (roundNo: number) => void;
    removeValues: (roundNo: number) => void;
  },
] => {
  const [storageState, setStorageState] = useState<IAnswerStorageValue>({});
  const [tempState, setTempState] = useState<IAnswerStorageValue>({});
  const [ids, setIds] = useState<number[]>([]);

  const setQuestionIds = useCallback(
    (questionIds: number[], roundNo: number) => {
      const newState: IAnswerStorageValue = {};

      questionIds.forEach(id => {
        newState[id] = newState[id] ?? {};
        newState[id][roundNo] = localStorage.getItem(
          `question_answer_${id}_${roundNo}`,
        );
      });
      setStorageState(newState);
      setTempState({ ...newState }); // copy object
      setIds(questionIds);

      return newState;
    },
    [],
  );

  const setTempValue = useCallback(
    (questionId: number, roundNo: number, value: string) => {
      setTempState(prevState => ({
        ...prevState,
        [questionId]: {
          ...prevState[questionId],
          [roundNo]: value,
        },
      }));
    },
    [],
  );

  const saveValues = useCallback(
    (roundNo: number) => {
      ids.forEach(id => {
        const value = tempState[id]?.[roundNo];
        if (value !== null) {
          localStorage.setItem(`question_answer_${id}_${roundNo}`, value);
        }
      });
      // copy tempState to storageState
      setStorageState({ ...tempState });
    },
    [ids, tempState],
  );

  const removeValues = useCallback(
    (roundNo: number) => {
      const newState: IAnswerStorageValue = {};
      ids.forEach(id => {
        localStorage.removeItem(`question_answer_${id}_${roundNo}`);
        if (newState[id]) {
          newState[id][roundNo] = null;
        }
      });

      setStorageState(newState);
      setTempState({ ...newState }); // copy object
    },
    [ids],
  );

  const answerStorage = useMemo(
    () => ({
      setQuestionIds,
      setTempValue,
      saveValues,
      removeValues,
    }),
    [setQuestionIds, setTempValue, saveValues, removeValues],
  );

  return [{ storageValue: storageState, tempValue: tempState }, answerStorage];
};

export default useAnswerStorage;
